import React, { useEffect, useState, useContext, useRef } from "react";
import QRCode from "qrcode.react";
import toastError from "../../errors/toastError";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Tab,
  Tabs,
  TextField,
  InputAdornment,
} from "@material-ui/core";
import PhoneIcon from "@material-ui/icons/Phone";
import QrCodeIcon from "@material-ui/icons/CropFree";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: 340,
    maxWidth: 440,
    width: "100%",
    margin: 0,
  },
  tabsRoot: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 42,
  },
  tab: {
    minHeight: 42,
    fontSize: "0.8rem",
    textTransform: "none",
    fontWeight: 500,
  },
  content: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: theme.spacing(2, 3, 1),
    minHeight: 280,
  },
  qrCodeContainer: {
    padding: theme.spacing(2),
    backgroundColor: "white",
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1.5),
    border: "1px solid #e0e0e0",
  },
  waitingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    minHeight: 200,
    justifyContent: "center",
  },
  instructions: {
    marginTop: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    fontSize: "0.82rem",
    lineHeight: 1.6,
    textAlign: "left",
    padding: theme.spacing(0, 0.5),
    width: "100%",
  },
  instructionItem: {
    marginBottom: theme.spacing(0.4),
  },
  pairingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(2),
    width: "100%",
    paddingTop: theme.spacing(1),
  },
  pairingCodeBox: {
    background: theme.palette.grey[100],
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 12,
    padding: theme.spacing(2, 3),
    marginTop: theme.spacing(1),
    width: "100%",
  },
  pairingCodeText: {
    fontFamily: "monospace",
    fontSize: "2.2rem",
    fontWeight: 700,
    letterSpacing: "0.15em",
    color: theme.palette.primary.dark,
  },
  timerText: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  timerDanger: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  phoneInput: {
    width: "100%",
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "0.8rem",
    marginTop: theme.spacing(0.5),
  },
}));

const PAIRING_TTL = 60; // seconds

const QrcodeModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);

  const [tab, setTab] = useState(0);
  const [qrCode, setQrCode] = useState("");
  const [retrying, setRetrying] = useState(false);

  // Pairing code state
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [pairingCode, setPairingCode] = useState("");
  const [pairingLoading, setPairingLoading] = useState(false);
  const [pairingTimer, setPairingTimer] = useState(0);
  const timerRef = useRef(null);
  const pairingCodeRef = useRef(""); // ref síncrono para uso dentro de callbacks de socket/timer

  // Wrapper que mantém ref e state em sincronia
  const setCode = (code) => {
    pairingCodeRef.current = code;
    setPairingCode(code);
  };

  // Load initial QR from DB
  useEffect(() => {
    if (!whatsAppId || !open) return;
    const fetchSession = async () => {
      try {
        const { data } = await api.get(`/whatsapp/${whatsAppId}`);
        setQrCode(data.qrcode || "");
        if (data.pairingCode) {
          setCode(data.pairingCode);
          setTab(1);
          startTimer();
        }
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatsAppId, open]);

  // Socket listener
  useEffect(() => {
    if (!whatsAppId || !open) return;
    const companyId = user.companyId;

    const onWhatsappData = (data) => {
      if (data.action !== "update" || data.session.id !== whatsAppId) return;
      const session = data.session;

      if (session.status === "CONNECTED") {
        clearTimer();
        onClose();
        return;
      }

      if (session.pairingCode && session.status === "PAIRING") {
        setCode(session.pairingCode);
        setPairingLoading(false);
        setTab(1);
        startTimer();
        return;
      }

      if (session.status === "DISCONNECTED" || session.status === "OPENING") {
        setPairingLoading(false);
        // Só limpa o código se não há nenhum sendo exibido (estado de loading)
        // Se já temos um código visível, ele persiste até o timer expirar naturalmente
        if (!pairingCodeRef.current) {
          clearTimer();
        }
      }

      setQrCode(session.qrcode || "");
    };

    socket.on(`company-${companyId}-whatsappSession`, onWhatsappData);
    return () => socket.off(`company-${companyId}-whatsappSession`, onWhatsappData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatsAppId, open, onClose, user.companyId, socket]);

  // Cleanup on unmount / close
  useEffect(() => {
    if (!open) {
      clearTimer();
      pairingCodeRef.current = "";
      setPairingCode("");
      setPairingLoading(false);
      setPhoneNumber("");
      setPhoneError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const startTimer = () => {
    clearTimer();
    setPairingTimer(PAIRING_TTL);
    timerRef.current = setInterval(() => {
      setPairingTimer((t) => {
        if (t <= 1) {
          clearTimer();
          // Expira: zera código via ref + state
          pairingCodeRef.current = "";
          setPairingCode("");
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  };

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleRetryQr = async () => {
    try {
      setRetrying(true);
      setQrCode("");
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    } finally {
      setRetrying(false);
    }
  };

  const validatePhone = (value) => {
    const digits = value.replace(/\D/g, "");
    if (!digits) return "Número obrigatório";
    if (digits.length < 10 || digits.length > 15) return "Informe com DDI: ex: 5519988280392";
    return "";
  };

  const handlePhoneChange = (e) => {
    const raw = e.target.value;
    setPhoneNumber(raw);
    if (phoneError) setPhoneError(validatePhone(raw));
  };

  const handleRequestPairingCode = async () => {
    const err = validatePhone(phoneNumber);
    if (err) {
      setPhoneError(err);
      return;
    }
    const cleanPhone = phoneNumber.replace(/\D/g, "");
    try {
      setPairingLoading(true);
      setCode("");
      clearTimer();
      await api.post(`/whatsapp/${whatsAppId}/pairing-code`, { phoneNumber: cleanPhone });
      // Code will arrive via socket event (session.pairingCode)
    } catch (err) {
      toastError(err);
      setPairingLoading(false);
    }
  };

  const renderQrTab = () => (
    <>
      {qrCode ? (
        <>
          <div className={classes.qrCodeContainer}>
            <QRCode value={qrCode} size={240} style={{ maxWidth: "100%", height: "auto" }} />
          </div>
          <Paper elevation={0} className={classes.instructions}>
            <div className={classes.instructionItem}>1. Abra o WhatsApp no celular</div>
            <div className={classes.instructionItem}>2. Toque em <strong>⋮ → Aparelhos Conectados</strong></div>
            <div className={classes.instructionItem}>3. Toque em <strong>Conectar um aparelho</strong></div>
            <div className={classes.instructionItem}>4. Aponte a câmera para o QR acima</div>
          </Paper>
        </>
      ) : (
        <div className={classes.waitingContainer}>
          <CircularProgress size={44} color="primary" />
          <Typography variant="body2" color="textSecondary">Aguardando QR Code...</Typography>
          <Typography variant="caption" color="textSecondary">Isso pode levar alguns segundos</Typography>
        </div>
      )}
    </>
  );

  const renderPairingTab = () => (
    <div className={classes.pairingContainer}>
      <Typography variant="body2" color="textSecondary" style={{ textAlign: "center" }}>
        Digite o número do celular que será conectado (com DDI)
      </Typography>

      <TextField
        className={classes.phoneInput}
        label="Número com DDI"
        variant="outlined"
        size="small"
        value={phoneNumber}
        onChange={handlePhoneChange}
        error={!!phoneError}
        helperText={phoneError || "Ex: 5519988280392"}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PhoneIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
        disabled={pairingLoading}
        onKeyDown={(e) => e.key === "Enter" && handleRequestPairingCode()}
      />

      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleRequestPairingCode}
        disabled={pairingLoading || !!pairingCode}
      >
        {pairingLoading ? <CircularProgress size={20} color="inherit" /> : "Gerar Código"}
      </Button>

      {pairingLoading && !pairingCode && (
        <Typography variant="caption" color="textSecondary">
          Aguardando código do WhatsApp...
        </Typography>
      )}

      {pairingCode && (
        <>
          <div className={classes.pairingCodeBox}>
            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
              Código de pareamento
            </Typography>
            <Typography className={classes.pairingCodeText}>{pairingCode}</Typography>
            <Typography
              className={`${classes.timerText} ${pairingTimer <= 15 ? classes.timerDanger : ""}`}
            >
              {pairingTimer > 0 ? `Expira em ${pairingTimer}s` : "Código expirado — gere um novo"}
            </Typography>
          </div>

          <Paper elevation={0} className={classes.instructions}>
            <div className={classes.instructionItem}>1. Abra o WhatsApp no celular</div>
            <div className={classes.instructionItem}>2. Toque em <strong>⋮ → Aparelhos Conectados</strong></div>
            <div className={classes.instructionItem}>3. Toque em <strong>Conectar um aparelho</strong></div>
            <div className={classes.instructionItem}>4. Toque em <strong>"Conectar com número de telefone"</strong></div>
            <div className={classes.instructionItem}>5. Escolha o país e confirme o número</div>
            <div className={classes.instructionItem}>6. Digite o código acima</div>
          </Paper>
        </>
      )}

      {!pairingCode && !pairingLoading && (
        <Paper elevation={0} className={classes.instructions}>
          <div className={classes.instructionItem}>1. Digite seu número com DDI (55 + DDD + número)</div>
          <div className={classes.instructionItem}>2. Clique em <strong>Gerar Código</strong></div>
          <div className={classes.instructionItem}>3. Aguarde o código de 8 caracteres</div>
          <div className={classes.instructionItem}>4. No WhatsApp: <strong>⋮ → Aparelhos → Conectar → Número</strong></div>
        </Paper>
      )}
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
      aria-labelledby="qr-code-dialog"
    >
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
        classes={{ root: classes.tabsRoot }}
      >
        <Tab icon={<QrCodeIcon fontSize="small" />} label="QR Code" classes={{ root: classes.tab }} />
        <Tab icon={<PhoneIcon fontSize="small" />} label="Código de Pareamento" classes={{ root: classes.tab }} />
      </Tabs>

      <DialogContent className={classes.content}>
        {tab === 0 ? renderQrTab() : renderPairingTab()}
      </DialogContent>

      <DialogActions style={{ justifyContent: "space-between", padding: "8px 16px 12px" }}>
        <Button onClick={onClose} color="default" size="small">
          Cancelar
        </Button>
        {tab === 0 ? (
          <Button
            onClick={handleRetryQr}
            color="primary"
            variant="outlined"
            size="small"
            disabled={retrying}
          >
            {retrying ? <CircularProgress size={16} /> : "Novo QR"}
          </Button>
        ) : pairingCode && pairingTimer === 0 ? (
          <Button
            onClick={handleRequestPairingCode}
            color="primary"
            variant="outlined"
            size="small"
            disabled={pairingLoading}
          >
            Gerar novo código
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);
